import { useMemo } from 'react';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { filterRsvpPage, shouldHideRsvpPage } from '@apps/guest/packages/layout-engine/common/utils/rsvpPageVisibility';
import { useRsvpDeadlineGuard } from '@apps/guest/packages/layout-engine/common/utils/useRsvpDeadlineGuard';
import { useFilterMigratedLegacyPages } from './useFilterMigratedLegacyPages';

type UseGuestSitePagesReturn = Readonly<{
  /** The pages every guest-facing nav surface — and Brannan's page resolution — should see. */
  pages: ReadonlyArray<EventPageFragment>;
  /** True while the migrated-custom-page feature flags are still resolving. */
  isMigratedFlagsLoading: boolean;
}>;

/**
 * The one page list a layout should render from. Composes the two filters every layout needs:
 * legacy pages migrated to custom pages are dropped, and the RSVP page is dropped when it is
 * effectively disabled or past its deadline.
 *
 * Layouts previously hand-assembled this three-stage pipeline, which is how two of the three ended
 * up with the same intermediate spelled differently (LEGRSVP-96). Both filters preserve the input
 * array by reference on a no-op, so the result stays referentially stable for `useAlohaScroll`.
 */
export const useGuestSitePages = (pages: ReadonlyArray<EventPageFragment>, hasEnabledLegacyRsvpQuestions: Maybe<boolean> | undefined): UseGuestSitePagesReturn => {
  const { filteredPages: pagesWithoutMigratedLegacy, isLoading: isMigratedFlagsLoading } = useFilterMigratedLegacyPages(pages);
  const { isRsvpClosedByDeadline } = useRsvpDeadlineGuard();

  // Deliberately memoized on the collapsed decision, not on the two reasons behind it: the reasons
  // resolve at different times, and once one of them hides the RSVP page the other flipping true
  // must not re-filter into a fresh, identical array. See `shouldHideRsvpPage`.
  const isRsvpPageHidden = shouldHideRsvpPage({ hasEnabledLegacyRsvpQuestions, isRsvpClosedByDeadline });
  const guestSitePages = useMemo(() => filterRsvpPage(pagesWithoutMigratedLegacy, isRsvpPageHidden), [pagesWithoutMigratedLegacy, isRsvpPageHidden]);

  return { pages: guestSitePages, isMigratedFlagsLoading };
};
